/**
 * WO#1 — Slot Economy domain foundation: GRID POWER (GP) capacity.
 *
 * GP = CONTENT CAPACITY. It is NOT currency, token, spendable balance or
 * consumable credit. Modules occupy GP while present; removing a module
 * releases its GP.
 *
 * FROZEN PRODUCT LAW (WO#1 §4/§6):
 *   - Capacity depends ONLY on the physical area of ONE continuous
 *     same-wallet territory. Tier does NOT affect GP.
 *   - Minimum publishable area = 400 world-units². No minimum side length.
 *   - Deterministic, integer/fixed-point arithmetic; final result floored.
 *
 * This module is the SINGLE authoritative GP capacity function. Do not
 * scatter GP anchor numbers or area thresholds elsewhere.
 */

/** Minimum continuous area (world-units²) required to publish content. */
export const MIN_PUBLISHABLE_AREA = 400;

/**
 * Macro #21 — Universal Basic Territory Identity. Basic Identity (Territory Name
 * + Brand Image / LOD0) is available to ANY legitimately owned holding regardless
 * of size — DECOUPLED from MIN_PUBLISHABLE_AREA (which still gates Canvas / GP /
 * Artwork content publishing). Any real owned territory has area >= 1.
 */
export const MIN_IDENTITY_AREA = 1;

/** True iff a legitimately owned holding may use Basic Identity (Name + Brand Image). */
export function isIdentityEligible(area: number): boolean {
  return Number.isFinite(area) && Math.floor(area) >= MIN_IDENTITY_AREA;
}

/** The full 1000×1000 Grid area. */
export const FULL_GRID_AREA = 1_000_000;

/**
 * Intentional product discontinuity (WO#1 §6): a single continuous
 * same-wallet territory covering the ENTIRE Grid grants EXACTLY 1000 GP.
 * This is NOT the smooth extrapolation of the linear-above-anchor slope
 * (which would yield 290). Do not smooth it away.
 */
export const FULL_GRID_GP = 1000;

/**
 * Above the last anchor (250,000) and below the full Grid, GP grows
 * linearly at +10 GP per +50,000 area === +1 GP per +5,000 area.
 */
export const ABOVE_ANCHOR_AREA_STEP = 5_000;

/**
 * Canonical piecewise-linear anchor table [area, GP]. Between adjacent
 * anchors GP is linearly interpolated and the final result floored.
 * Ordered strictly ascending by area.
 */
export const GP_ANCHORS: ReadonlyArray<readonly [number, number]> = [
  [400, 4],
  [900, 6],
  [2_500, 12],
  [5_625, 20],
  [10_000, 30],
  [22_500, 45],
  [40_000, 60],
  [90_000, 90],
  [250_000, 140],
] as const;

/** True iff a continuous territory of `area` may publish content. */
export function isPublishable(area: number): boolean {
  return Number.isFinite(area) && Math.floor(area) >= MIN_PUBLISHABLE_AREA;
}

/**
 * Deterministic GP capacity for a continuous same-wallet territory of the
 * given physical `area` (world-units²).
 *
 *   - area < 400            → 0 (not content-publishable)
 *   - area == 1,000,000     → 1000 (exact special case)
 *   - 250,000 ≤ area < 1e6  → 140 + floor((area - 250,000) / 5,000)
 *   - 400 ≤ area < 250,000  → floor of piecewise-linear interpolation
 *
 * Uses integer arithmetic (floor over integer numerator/denominator) so
 * independent implementations agree bit-for-bit.
 */
export function gridPowerCapacity(areaInput: number): number {
  if (!Number.isFinite(areaInput)) return 0;
  // Micro-hardening: reject an impossible RAW area BEFORE flooring, so a
  // non-integer > 1e6 (e.g. 1_000_000.5) can never be floored down into the
  // exact full-Grid special case. ONLY the exact physical area 1,000,000
  // receives 1000 GP; any raw areaInput > 1,000,000 is invalid.
  if (areaInput > FULL_GRID_AREA) {
    throw new RangeError(
      `gridPowerCapacity: raw area ${areaInput} exceeds FULL_GRID_AREA ${FULL_GRID_AREA} (invalid domain state)`,
    );
  }
  const area = Math.floor(areaInput);
  if (area < MIN_PUBLISHABLE_AREA) return 0;
  // Exact full-Grid special case (EXACTLY 1,000,000 → 1000 GP).
  if (area === FULL_GRID_AREA) return FULL_GRID_GP;

  const lastAnchor = GP_ANCHORS[GP_ANCHORS.length - 1];
  const [lastArea, lastGp] = lastAnchor;
  if (area >= lastArea) {
    return lastGp + Math.floor((area - lastArea) / ABOVE_ANCHOR_AREA_STEP);
  }

  for (let i = 0; i < GP_ANCHORS.length - 1; i++) {
    const [aLow, gLow] = GP_ANCHORS[i];
    const [aHigh, gHigh] = GP_ANCHORS[i + 1];
    if (area >= aLow && area <= aHigh) {
      const numerator = (area - aLow) * (gHigh - gLow);
      return gLow + Math.floor(numerator / (aHigh - aLow));
    }
  }

  // Unreachable for area in [400, 250000); defensive.
  return 0;
}
